import type { Page } from '../../store/editor.store'

/**
 * The last-persisted project snapshot, so autosave can diff and save only what
 * actually changed. It's a module singleton (one project per editor page load)
 * set authoritatively when the project finishes loading — NOT captured off a
 * random store change, which could otherwise snapshot the pre-load default pages
 * and make every real page look "changed".
 */
export type Baseline = {
  pages: Map<string, Page>
  order: string[]
  content: unknown
  theme: unknown
  name: string | null
  /** False until the project has loaded — autosave must not persist before this. */
  ready: boolean
  /**
   * La versión en el servidor de cada página y del proyecto (tema y CMS) sobre
   * la que se está editando. Viajan con cada guardado: si lo guardado ya es
   * otra cosa —otra pestaña, otro dispositivo, otro miembro—, el servidor
   * responde 409 en vez de pisarlo. Una página sin versión es nueva.
   */
  pageRevisions: Map<string, string>
  dataRevision: string | null
}

const baseline: Baseline = {
  pages: new Map(),
  order: [],
  content: undefined,
  theme: undefined,
  name: null,
  ready: false,
  pageRevisions: new Map(),
  dataRevision: null,
}

/** Called right after the project loads — marks the loaded state as persisted. */
export function setBaseline(pages: Page[], content: unknown, theme: unknown, name: string | null): void {
  baseline.pages = new Map(pages.map((p) => [p.id, p]))
  baseline.order = pages.map((p) => p.id)
  baseline.content = content
  baseline.theme = theme
  baseline.name = name
  baseline.ready = true
}

/** Las versiones del servidor que se acaban de leer al abrir el proyecto. */
export function setRevisions(pageRevisions: Record<string, string>, dataRevision: string | null): void {
  baseline.pageRevisions = new Map(Object.entries(pageRevisions))
  baseline.dataRevision = dataRevision
}

export function getBaseline(): Baseline {
  return baseline
}

/**
 * Si hay algo en el editor que todavía no está guardado.
 *
 * La MISMA regla que usa el autoguardado para decidir qué mandar —páginas
 * distintas de las guardadas, páginas quitadas, orden, y lo del proyecto—, y no
 * la bandera de «sucio». Esa bandera la levanta cualquier cambio del store,
 * también los de la carga, así que preguntar por ella avisaba de cambios sin
 * guardar a quien acababa de abrir el proyecto y no había tocado nada.
 *
 * Sin línea de base todavía, no hay nada pendiente: no se sabe contra qué
 * comparar, y el autoguardado tampoco manda nada en ese estado.
 */
export function hasUnsavedChanges(
  current: { pages: Page[]; content: unknown; theme: unknown; name: string | null },
  base: Baseline = baseline,
): boolean {
  if (!base.ready) return false
  const ids = current.pages.map((page) => page.id)
  return current.pages.some((page) => base.pages.get(page.id) !== page)
    || [...base.pages.keys()].some((id) => !ids.includes(id))
    || ids.length !== base.order.length
    || ids.some((id, index) => id !== base.order[index])
    || current.content !== base.content
    || current.theme !== base.theme
    || current.name !== base.name
}
